import {
	Component,
	inject,
	Input,
	OnChanges,
	SimpleChanges,
	ChangeDetectionStrategy,
} from '@angular/core';

import { ApplicationService } from '@lmc-app/core/services/application.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { ScheduleCallFormComponent } from './schedule-call-form.component';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import { CommonDialog } from '@lmc-app/shared/components/dialog/common-dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { DateUtil } from '@lmc-app/shared/utils/date.util';
import { ApplicationCallPurpose } from '@lmc-app/shared/enum/application-call-purpose.enum';
import { ApplicationCallStatus } from '@lmc-app/shared/enum/application-call-status.enum';
import { ApplicationCallRequest } from '@lmc-app/shared/interface/request/application-call.request';
import { User } from '@lmc-app/shared/interface/user.interface';
import {
	MatSlideToggleChange,
	MatSlideToggle,
} from '@angular/material/slide-toggle';
import dayjs from 'dayjs';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatButton, MatIconButton } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { StateDisplayComponent } from '../../../shared/components/state-display/state-display.component';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
	selector: 'app-call',
	templateUrl: './call.component.html',
	styleUrls: ['./call.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [],
	imports: [
		MatCard,
		MatCardContent,
		MatButton,
		NgIf,
		MatIcon,
		MatSlideToggle,
		StateDisplayComponent,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatIconButton,
		MatTooltip,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
	],
})
export class CallComponent implements OnChanges {
	@Input() application: any;
	@Input() currentUser: User;

	displayedColumnsApplicationCalls: string[] = [
		'applicant',
		'subject',
		'purpose',
		'dateTime',
		'status',
		'createdBy',
		'ownedBy',
		'action',
	];

	dataSourceApplicationCalls = new MatTableDataSource<ApplicationCall>();

	// Loading states
	isLoading = true;
	hasError = false;
	errorMessage = '';

	// Filter state
	showPastDueCall = false;

	protected readonly Applicant = Applicant;
	protected readonly DateUtil = DateUtil;
	protected readonly ApplicationCallPurpose = ApplicationCallPurpose;
	protected readonly ApplicationCallStatus = ApplicationCallStatus;

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _dialog = inject(MatDialog);
	private readonly _snackBar = inject(MatSnackBar);

	constructor(private readonly applicationService: ApplicationService) {}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return (
			this.isLoading ||
			this.hasError ||
			this.dataSourceApplicationCalls.data.length === 0
		);
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		if (this.hasError) return 'error';
		return 'empty';
	}

	get showTable(): boolean {
		return (
			!this.isLoading &&
			!this.hasError &&
			this.dataSourceApplicationCalls.data.length > 0
		);
	}

	ngOnChanges(changes: SimpleChanges) {
		this.application = changes.application?.currentValue;
		if (this.application) {
			this.getCalls();
		}
		if (changes.currentUser?.currentValue) {
			this.currentUser = changes.currentUser?.currentValue;
		}
	}

	openScheduleCallBottomSheet(applicationCall?: ApplicationCall) {
		this._bottomSheet
			.open(ScheduleCallFormComponent, {
				panelClass: 'bottom-sheet-width',
				data: {
					application: this.application,
					applicationCall: applicationCall,
					currentUser: this.currentUser,
				},
			})
			.afterDismissed()
			.subscribe((result) => {
				if (result) {
					// Refresh data after bottom sheet operations
					this.getCalls();
				}
			});
	}

	getCalls() {
		this.isLoading = true;
		this.hasError = false;
		this.errorMessage = '';

		// Calculate startTime if filter is active
		const startTime = !this.showPastDueCall
			? dayjs().startOf('day').toISOString()
			: undefined;

		this.applicationService
			.getApplicationCalls(this.application.id, startTime)
			.subscribe({
				next: (res) => {
					this.dataSourceApplicationCalls.data = res || [];
					this.isLoading = false;
				},
				error: (response) => {
					console.error(response);
					this.hasError = true;
					this.errorMessage = 'Failed to load calls';
					this.isLoading = false;
					this._snackBar.open(
						response.error.message,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
	}

	onDateRangeToggle(event: MatSlideToggleChange) {
		this.showPastDueCall = event.checked;
		this.getCalls();
	}

	private updateLocalData(updatedCall: ApplicationCall) {
		const data = [...this.dataSourceApplicationCalls.data];
		const index = data.findIndex((call) => call.uuid === updatedCall.uuid);

		if (index !== -1) {
			data[index] = { ...updatedCall };
			this.dataSourceApplicationCalls.data = data;
		}
	}

	private addLocalData(newCall: ApplicationCall) {
		const data = [...this.dataSourceApplicationCalls.data];
		data.push(newCall);
		this.dataSourceApplicationCalls.data = data;
	}

	private removeLocalData(callUuid: string) {
		const data = this.dataSourceApplicationCalls.data.filter(
			(call) => call.uuid !== callUuid
		);
		this.dataSourceApplicationCalls.data = data;
	}

	editCall(applicationCall: ApplicationCall) {
		this.openScheduleCallBottomSheet(applicationCall);
	}

	completeCall(applicationCall: ApplicationCall) {
		this._dialog
			.open(CommonDialog, {
				data: {
					title: 'Are you sure?',
					content: 'Complete this call?',
				},
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const originalStatus = applicationCall.status;
					applicationCall.status = ApplicationCallStatus.COMPLETED;

					// Update local data immediately for better UX
					this.updateLocalData(applicationCall);

					const request: ApplicationCallRequest = {
						status: ApplicationCallStatus.COMPLETED,
						userId: this.currentUser.employeeID,
					};

					this.applicationService
						.updateApplicationCall(
							this.application.id,
							applicationCall.uuid,
							request
						)
						.subscribe({
							next: () => {
								// Refresh data from server to ensure consistency
								this.getCalls();
							},
							error: (error) => {
								// Revert local change on error
								applicationCall.status = originalStatus;
								this.updateLocalData(applicationCall);

								console.error(error);
								this._snackBar.open(
									error,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	cancelCall(applicationCall: ApplicationCall) {
		this._dialog
			.open(CommonDialog, {
				data: { title: 'Are you sure?', content: 'Cancel this call?' },
			})
			.afterClosed()
			.subscribe((result) => {
				if (result) {
					const originalStatus = applicationCall.status;
					applicationCall.status = ApplicationCallStatus.CANCELLED;

					// Update local data immediately for better UX
					this.updateLocalData(applicationCall);

					const request: ApplicationCallRequest = {
						status: ApplicationCallStatus.CANCELLED,
						userId: this.currentUser.employeeID,
					};

					this.applicationService
						.updateApplicationCall(
							this.application.id,
							applicationCall.uuid,
							request
						)
						.subscribe({
							next: () => {
								// Refresh data from server to ensure consistency
								this.getCalls();
							},
							error: (error) => {
								// Revert local change on error
								applicationCall.status = originalStatus;
								this.updateLocalData(applicationCall);

								console.error(error);
								this._snackBar.open(
									error,
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}
}
